import { ChartingLayerKey } from '@dataTypes/chartingPreferences.type'

export type ChartingLayersState = Record<ChartingLayerKey, boolean>

export const CHARTING_LAYER_KEYS: ChartingLayerKey[] = [
  'dental',
  'endo',
  'perio',
]

export const DEFAULT_CHARTING_VIEW_LAYERS: ChartingLayerKey[] = [
  'dental',
  'endo',
]

const LEGACY_DEFAULT_CHARTING_VIEW_MAP: Record<string, ChartingLayerKey[]> = {
  perio_endo: ['endo', 'perio'],
  dental: ['dental'],
  perio: ['perio'],
  endo: ['endo'],
}

const parseLayerTokens = (value: string): ChartingLayerKey[] =>
  value
    .split(',')
    .map((token) => token.trim())
    .filter((token): token is ChartingLayerKey =>
      CHARTING_LAYER_KEYS.includes(token as ChartingLayerKey),
    )

const orderLayers = (layers: ChartingLayerKey[]): ChartingLayerKey[] => {
  const selected = new Set(layers)
  return CHARTING_LAYER_KEYS.filter((layer) => selected.has(layer))
}

/** Dedupe and order layer keys; preserves an explicit empty selection. */
export const sanitizeDefaultChartingView = (
  value: ChartingLayerKey[],
): ChartingLayerKey[] => orderLayers(Array.from(new Set(value)))

export const normalizeDefaultChartingView = (
  value: ChartingLayerKey[] | string | undefined | null,
  fallback: ChartingLayerKey[] = DEFAULT_CHARTING_VIEW_LAYERS,
): ChartingLayerKey[] => {
  if (Array.isArray(value)) {
    return sanitizeDefaultChartingView(value)
  }

  const trimmed = value?.trim() ?? ''
  if (!trimmed) {
    return fallback
  }

  const legacyValue = LEGACY_DEFAULT_CHARTING_VIEW_MAP[trimmed]
  const sourceLayers = legacyValue ?? parseLayerTokens(trimmed)
  const uniqueLayers = orderLayers(Array.from(new Set(sourceLayers)))

  if (uniqueLayers.length === 0) {
    return fallback
  }

  return uniqueLayers
}

export const defaultChartingViewToLayers = (
  value: ChartingLayerKey[] | string | undefined | null,
): ChartingLayersState => {
  const normalized = normalizeDefaultChartingView(value)
  const selectedLayers = new Set(normalized)

  return {
    dental: selectedLayers.has('dental'),
    endo: selectedLayers.has('endo'),
    perio: selectedLayers.has('perio'),
  }
}

export const layersToDefaultChartingView = (
  layers: ChartingLayersState,
): ChartingLayerKey[] => {
  const selected = CHARTING_LAYER_KEYS.filter((layer) => layers[layer])
  return normalizeDefaultChartingView(selected)
}

export const defaultChartingViewToCommaValue = (
  value: ChartingLayerKey[] | string | undefined | null,
): string => normalizeDefaultChartingView(value).join(',')

export const commaValueToDefaultChartingView = (
  value: string,
): ChartingLayerKey[] => normalizeDefaultChartingView(value)
